import * as XLSX from 'xlsx-js-style';

/**
 * Onarım Kapanış Formu Excel Export
 *
 * PDF jeneratörüyle (RepairClosingPdfFormPdfMake) aynı veri yapısını ve
 * aynı kullanım imzasını paylaşır:
 *     const xls = new RepairClosingExcelForm();
 *     const wb  = xls.generate(data, translations, options);
 *     xls.download(wb, 'onarim-kapanis-formu.xlsx');
 *
 * Tek bir kapanış formunu, başlık + müşteri bilgisi + onarım bilgisi +
 * tahmini ücret + parça/işçilik tablosu + toplamlar bölümlerini içeren
 * tek sayfalık bir Excel dökümanı olarak üretir.
 */
class RepairClosingExcelForm {

    formatNumber(value, decimals = 2) {
        if (value === null || value === undefined || isNaN(value)) return 0;
        const num = Number(value);
        if (num < 0) return 0;
        return Number(num.toFixed(decimals));
    }

    /**
     * Garanti tipi göstergesi - PDF jeneratörüyle birebir aynı kural
     */
    getWarrantyTypeIndicator(tip) {
        if (!tip) return 'M';
        const t = tip.toLowerCase();
        if (t.includes('karsan') && t.includes('garanti')) return 'K';
        if (t.includes('müşteri') || t.includes('musteri') || t.includes('öder')) return 'M';
        if (t.includes('servis') && t.includes('garanti')) return 'S';
        return 'M';
    }

    getPriceByWarrantyType(price, tip) {
        return this.getWarrantyTypeIndicator(tip) === 'M' ? this.formatNumber(price) : 0;
    }

    calculateAmount(price, quantity, tip) {
        const ind = this.getWarrantyTypeIndicator(tip);
        if (ind === 'K' || ind === 'S') return 0;
        if (!price) return 0;
        if (!quantity) return this.formatNumber(price);
        return this.formatNumber(price * quantity);
    }

    calculateDiscountPercent(price, acceptedPrice, quantity, tip) {
        const ind = this.getWarrantyTypeIndicator(tip);
        if (ind === 'K' || ind === 'S') return 0;
        const amount = (price || 0) * (quantity || 1);
        if (amount === 0) return 0;
        const discount = ((amount - (acceptedPrice || 0)) / amount) * 100;
        return this.formatNumber(Math.max(0, discount));
    }

    formatDate(value, withTime = false) {
        if (!value) return '-';
        const d = new Date(value);
        if (isNaN(d.getTime())) return '-';
        return withTime
            ? d.toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' })
            : d.toLocaleDateString('tr-TR');
    }

    get styles() {
        const border = {
            top:    { style: 'thin', color: { rgb: 'DEE2E6' } },
            bottom: { style: 'thin', color: { rgb: 'DEE2E6' } },
            left:   { style: 'thin', color: { rgb: 'DEE2E6' } },
            right:  { style: 'thin', color: { rgb: 'DEE2E6' } }
        };

        return {
            title: {
                font: { name: 'Calibri', sz: 16, bold: true, color: { rgb: '273A81' } },
                alignment: { horizontal: 'right', vertical: 'center' }
            },
            brand: {
                font: { name: 'Calibri', sz: 14, bold: true, color: { rgb: '76777A' } },
                alignment: { horizontal: 'left', vertical: 'center' }
            },
            headerInfo: {
                font: { name: 'Calibri', sz: 9, color: { rgb: '505050' } },
                alignment: { horizontal: 'right', vertical: 'center' }
            },
            customerName: {
                font: { name: 'Calibri', sz: 12, bold: true, color: { rgb: '2C3E50' } },
                alignment: { horizontal: 'left', vertical: 'center' }
            },
            customerDetail: {
                font: { name: 'Calibri', sz: 9 },
                alignment: { horizontal: 'left', vertical: 'center' }
            },
            fieldLabel: {
                font: { name: 'Calibri', sz: 8, color: { rgb: '666666' } },
                alignment: { horizontal: 'left', vertical: 'center', wrapText: true },
                border
            },
            fieldValue: {
                font: { name: 'Calibri', sz: 9, bold: true },
                alignment: { horizontal: 'left', vertical: 'center', wrapText: true },
                border
            },
            tableHeader: {
                font: { name: 'Calibri', sz: 9, bold: true },
                alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
                fill: { patternType: 'solid', fgColor: { rgb: 'F8F9FA' } },
                border
            },
            tableHeaderAccent: {
                font: { name: 'Calibri', sz: 9, bold: true },
                alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
                fill: { patternType: 'solid', fgColor: { rgb: 'FFF3E0' } },
                border
            },
            tableCell: {
                font: { name: 'Calibri', sz: 9 },
                alignment: { horizontal: 'left', vertical: 'center', wrapText: true },
                border
            },
            tableCellCenter: {
                font: { name: 'Calibri', sz: 9 },
                alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
                border
            },
            tableCellRight: {
                font: { name: 'Calibri', sz: 9 },
                alignment: { horizontal: 'right', vertical: 'center', wrapText: true },
                border,
                numFmt: '#,##0.00'
            },
            tableCellAccent: {
                font: { name: 'Calibri', sz: 9, color: { rgb: 'E65100' } },
                alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
                border
            },
            totalLabel: {
                font: { name: 'Calibri', sz: 10 },
                alignment: { horizontal: 'right', vertical: 'center' }
            },
            totalValue: {
                font: { name: 'Calibri', sz: 10 },
                alignment: { horizontal: 'right', vertical: 'center' },
                numFmt: '#,##0.00'
            },
            totalLabelBold: {
                font: { name: 'Calibri', sz: 11, bold: true },
                alignment: { horizontal: 'right', vertical: 'center' }
            },
            totalValueBold: {
                font: { name: 'Calibri', sz: 11, bold: true, color: { rgb: '273A81' } },
                alignment: { horizontal: 'right', vertical: 'center' },
                numFmt: '#,##0.00'
            },
            legend: {
                font: { name: 'Calibri', sz: 8, color: { rgb: '666666' } },
                alignment: { horizontal: 'left', vertical: 'center' }
            }
        };
    }

    // -----------------------------------------------------------------
    // Workbook oluşturma
    // -----------------------------------------------------------------

    /**
     * @param {Object} data         - PDF ile aynı veri objesi
     * @param {Object} translations - Çeviriler (opsiyonel)
     * @param {Object} options      - { sheetName }
     * @returns {Object} XLSX workbook
     */
    generate(data = {}, translations = {}, options = {}) {
        const t = translations;
        const s = this.styles;

        // ws_data: 2 boyutlu hücre matrisi. Her hücre {v, s} formatında.
        const rows = [];
        const merges = [];
        const colCount = 9; // Parça tablosu 9 sütun - genel grid bunun üstüne kurulu

        // Yardımcı: stilli hücre
        const C = (v, style) => ({ v: v ?? '', s: style });
        const empty = () => ({ v: '', s: {} });
        const padRow = (cells) => {
            const r = [...cells];
            while (r.length < colCount) r.push(empty());
            return r;
        };

        // ---------------- Başlık ----------------
        const reportDate = data.reportDate || new Date().toLocaleDateString('tr-TR');
        const reportTime = data.reportTime || new Date().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
        const reportCode = data.reportCode || data.onarimNo || data.id || '-';
        const reportReceiver = data.reportReceiver || data.receivingPerson || '-';

        // Satır 0: KARSAN ........... Onarım Kapanış Formu
        rows.push(padRow([
            C('KARSAN', s.brand), empty(), empty(),
            empty(), empty(), empty(), empty(), empty(),
            C(t.title || 'Onarım Kapanış Formu', s.title)
        ]));
        merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: 2 } });
        merges.push({ s: { r: 0, c: 3 }, e: { r: 0, c: 8 } });

        // Satır 1-4: Tarih/Saat/Kod/Alan bilgileri sağda
        const headerInfoLines = [
            `${t.reportDate || 'Rapor Tarihi'} : ${reportDate}`,
            `${t.reportTime || 'Rapor Saati'} : ${reportTime}`,
            `${t.reportCode || 'Rapor Kodu'} : ${reportCode}`,
            `${t.reportReceiver || 'Raporu Alan'} : ${reportReceiver}`
        ];
        headerInfoLines.forEach((line, i) => {
            const r = rows.length;
            rows.push(padRow([
                empty(), empty(), empty(), empty(), empty(),
                empty(), empty(), empty(),
                C(line, s.headerInfo)
            ]));
            merges.push({ s: { r, c: 5 }, e: { r, c: 8 } });
        });

        // Boş satır
        rows.push(padRow([]));

        // ---------------- Müşteri Bilgisi ----------------
        const customerName = data.customerNameSurname || data.adSoyad || '';
        const customerBranch = data.customerBranch || data.fullName || '';

        let r = rows.length;
        rows.push(padRow([C(customerName, s.customerName)]));
        merges.push({ s: { r, c: 0 }, e: { r, c: 8 } });

        if (data.customerAddress) {
            r = rows.length;
            rows.push(padRow([C(data.customerAddress, s.customerDetail)]));
            merges.push({ s: { r, c: 0 }, e: { r, c: 8 } });
        }

        if (customerBranch) {
            r = rows.length;
            rows.push(padRow([C(customerBranch, s.customerDetail)]));
            merges.push({ s: { r, c: 0 }, e: { r, c: 8 } });
        }

        rows.push(padRow([]));

        // ---------------- Onarım Bilgisi ----------------
        const repairDate = data.repairDate || data.createdDate
            ? this.formatDate(data.repairDate || data.createdDate)
            : new Date().toLocaleDateString('tr-TR');
        const repairTime = data.repairTime || new Date().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
        const invoiceDate = data.invoiceDate ? this.formatDate(data.invoiceDate) : '-';

        const buildLabelValueRow = (cellsSpec) => {
            const labelRow = [];
            const valueRow = [];
            let colIdx = 0;
            const labelRowIdx = rows.length;
            const valueRowIdx = rows.length + 1;

            cellsSpec.forEach(({ label, value, span }) => {
                labelRow.push(C(label, s.fieldLabel));
                valueRow.push(C(value ?? '-', s.fieldValue));
                for (let i = 1; i < span; i++) {
                    labelRow.push(empty());
                    valueRow.push(empty());
                }
                if (span > 1) {
                    merges.push({ s: { r: labelRowIdx, c: colIdx }, e: { r: labelRowIdx, c: colIdx + span - 1 } });
                    merges.push({ s: { r: valueRowIdx, c: colIdx }, e: { r: valueRowIdx, c: colIdx + span - 1 } });
                }
                colIdx += span;
            });

            rows.push(padRow(labelRow));
            rows.push(padRow(valueRow));
        };

        // Satır 1: Onarım No | Tarih | Saat | Plaka | KM   (2+2+1+2+2 = 9)
        buildLabelValueRow([
            { label: t.repairNo      || 'ONARIM NO',     value: data.repairNo || data.onarimNo || data.id, span: 2 },
            { label: t.repairDate    || 'TARİH',         value: repairDate,                                 span: 2 },
            { label: t.repairTime    || 'SAAT',          value: repairTime,                                 span: 1 },
            { label: t.licensePlate  || 'PLAKA',         value: data.licensePlate,                          span: 2 },
            { label: t.km            || 'KM',            value: data.kmInformation,                         span: 2 }
        ]);

        // Satır 2: Şasi No (3) | Garanti/Model (2) | Fatura Tarihi (2) | Fatura No (2)  = 9
        buildLabelValueRow([
            { label: t.chassisNo     || 'ŞASİ NO',       value: data.chassisNo,                             span: 3 },
            { label: t.vehicleType   || 'Garanti Tipi',  value: data.vehicleType || data.model,             span: 2 },
            { label: t.invoiceDate   || 'FATURA TARİHİ', value: invoiceDate,                                span: 2 },
            { label: t.invoiceNo     || 'FATURA NO',     value: data.invoiceNo,                             span: 2 }
        ]);

        // Satır 3: Müşteri Beyanı tam genişlik
        buildLabelValueRow([
            { label: t.arrivalReason || 'Müşteri Beyanı',
              value: data.arrivalReason || data.customerStatement, span: 9 }
        ]);

        rows.push(padRow([]));

        // ---------------- Tahmini Ücret + İskonto ----------------
        const laborPrice    = this.formatNumber(data.estimatedLabor     || data.totalLaborAcceptedPrice    || data.totalLaborPrice);
        const partPrice     = this.formatNumber(data.estimatedPart      || data.totalItemAcceptedPrice     || data.totalItemPrice);
        const otherPrice    = this.formatNumber(data.estimatedOtherCost || data.totalOtherCostPrice);
        const grandTotal    = this.formatNumber(
            data.estimatedTotal || data.totalDiscountedPrice ||
            ((data.totalLaborAcceptedPrice || data.totalLaborPrice || 0) +
             (data.totalItemAcceptedPrice  || data.totalItemPrice  || 0) +
             (data.totalOtherCostPrice     || 0))
        );
        const partsDiscount = data.partsDiscountRate != null ? `%${this.formatNumber(data.partsDiscountRate)}` : '-';
        const laborDiscount = data.laborDiscountRate != null ? `%${this.formatNumber(data.laborDiscountRate)}` : '-';

        const costSpec = [
            { label: t.labor              || 'İŞÇİLİK',       value: laborPrice,        span: 1, accent: false },
            { label: t.sparePart          || 'YEDEK PARÇA',   value: partPrice,         span: 1, accent: false },
            { label: t.otherCosts         || 'DİĞER MALİYET', value: otherPrice,        span: 2, accent: false },
            { label: t.total              || 'TOPLAM',        value: grandTotal,        span: 1, accent: false, bold: true },
            { label: t.laborDiscountRate  || 'İŞÇ. İSK.%',    value: laborDiscount,     span: 1, accent: true  },
            { label: t.partsDiscountRate  || 'PRÇ. İSK.%',    value: partsDiscount,     span: 2, accent: true  },
            { label: t.approvedBy         || 'ONAY VEREN',    value: data.approvedBy,   span: 1, accent: false }
        ];

        const costHdrRow = [];
        const costValRow = [];
        let costCol = 0;
        const costHdrIdx = rows.length;
        const costValIdx = rows.length + 1;

        costSpec.forEach(({ label, value, span, accent, bold }) => {
            costHdrRow.push(C(label, accent ? s.tableHeaderAccent : s.tableHeader));
            const isNumber = typeof value === 'number';
            costValRow.push(C(value ?? '', {
                ...(isNumber ? s.tableCellRight : s.tableCellCenter),
                ...(accent ? { font: { ...s.tableCell.font, color: { rgb: 'E65100' } } } : {}),
                ...(bold ? { font: { ...s.tableCell.font, bold: true } } : {})
            }));
            for (let i = 1; i < span; i++) {
                costHdrRow.push(empty());
                costValRow.push(empty());
            }
            if (span > 1) {
                merges.push({ s: { r: costHdrIdx, c: costCol }, e: { r: costHdrIdx, c: costCol + span - 1 } });
                merges.push({ s: { r: costValIdx, c: costCol }, e: { r: costValIdx, c: costCol + span - 1 } });
            }
            costCol += span;
        });

        rows.push(padRow(costHdrRow));
        rows.push(padRow(costValRow));

        rows.push(padRow([]));

        // ---------------- Teslim Bilgileri ----------------
        buildLabelValueRow([
            { label: t.deliveredBy   || 'Teslim Eden',          value: data.deliveredBy || data.receivingDeliveryPerson, span: 3 },
            { label: t.deliveryPhone || 'Telefon',              value: data.deliveryPhone,                                 span: 3 },
            { label: t.willReceive   || 'Aracı Teslim Alacak',  value: data.receivedBy || data.receivingPerson,            span: 3 }
        ]);

        rows.push(padRow([]));

        // ---------------- Notlar (2x2 grid) ----------------
        const noteRow = (leftLabel, leftValue, rightLabel, rightValue) => {
            const lblIdx = rows.length;
            const valIdx = rows.length + 1;
            // label satırı
            const labelCells = [
                C(leftLabel, s.fieldLabel), empty(), empty(), empty(),
                C(rightLabel, s.fieldLabel), empty(), empty(), empty(), empty()
            ];
            const valueCells = [
                C(leftValue || '', s.fieldValue), empty(), empty(), empty(),
                C(rightValue || '', s.fieldValue), empty(), empty(), empty(), empty()
            ];
            rows.push(labelCells);
            rows.push(valueCells);
            merges.push({ s: { r: lblIdx, c: 0 }, e: { r: lblIdx, c: 3 } });
            merges.push({ s: { r: lblIdx, c: 4 }, e: { r: lblIdx, c: 8 } });
            merges.push({ s: { r: valIdx, c: 0 }, e: { r: valIdx, c: 3 } });
            merges.push({ s: { r: valIdx, c: 4 }, e: { r: valIdx, c: 8 } });
        };

        noteRow(
            t.customerComplaints || 'Müşteri İtirazları',  data.customerComplaints,
            t.serviceNotes       || 'Servis Açıklamaları', data.serviceNote || data.customerStatement
        );
        noteRow(
            t.closingNotes       || 'Kapanış Notları',     data.closingNotes || data.customerStatement,
            t.failureSolution    || 'Arıza Çözümü',        data.failureSolution
        );

        rows.push(padRow([]));

        // ---------------- Parça/İşçilik Tablosu ----------------
        const headers = [
            t.warrantyType || 'Tip',
            t.orderNo      || 'O.T.',
            t.partNo       || 'Parça/İşç. No',
            t.description  || 'Açıklama',
            t.quantity     || 'Adet',
            t.unitPrice    || 'B.Fiyat',
            t.amount       || 'Tutar',
            t.discount     || 'İnd%',
            t.netTotal     || 'Net T.'
        ];
        rows.push(headers.map(h => C(h, s.tableHeader)));

        const details = data.details || [];
        if (details.length === 0) {
            const r0 = rows.length;
            rows.push(padRow([C(t.noData || 'Veri Yok', s.tableCellCenter)]));
            merges.push({ s: { r: r0, c: 0 }, e: { r: r0, c: 8 } });
        } else {
            details.forEach((d) => {
                const ind = this.getWarrantyTypeIndicator(d.tip);
                rows.push([
                    C(ind, { ...s.tableCellCenter, font: { ...s.tableCellCenter.font, bold: true } }),
                    C(d.type || '-', s.tableCellCenter),
                    C(d.code || '-', s.tableCellCenter),
                    C(d.description || '-', s.tableCell),
                    C(d.quantity ?? d.laborTime ?? '-', s.tableCellCenter),
                    C(this.getPriceByWarrantyType(d.price, d.tip), s.tableCellRight),
                    C(this.calculateAmount(d.price, d.quantity, d.tip), s.tableCellRight),
                    C(this.calculateDiscountPercent(d.price, d.acceptedPrice, d.quantity, d.tip), s.tableCellCenter),
                    C(this.getPriceByWarrantyType(d.acceptedPrice, d.tip), s.tableCellRight)
                ]);
            });
        }

        rows.push(padRow([]));

        // ---------------- Garanti Tipi Lejant ----------------
        const legendText =
            `K : ${t.karsanWarranty  || 'Karsan Garantili'}     ` +
            `M : ${t.customerPays    || 'Müşteri Öder'}     ` +
            `S : ${t.serviceWarranty || 'Servis Garantili'}`;
        const legendIdx = rows.length;
        rows.push(padRow([C(legendText, s.legend)]));
        merges.push({ s: { r: legendIdx, c: 0 }, e: { r: legendIdx, c: 8 } });

        rows.push(padRow([]));

        // ---------------- Toplamlar (sağ alt blok) ----------------
        let closingTotal    = data.totalPrice           || 0;
        let discountedTotal = data.totalDiscountedPrice || 0;
        const kdvRate       = data.isDomestic ? 0.20 : 0;

        // Eğer toplam yoksa detaydan hesapla (PDF ile aynı kural)
        if (details.length > 0 && closingTotal === 0) {
            const customerPaid = details.filter(d => this.getWarrantyTypeIndicator(d.tip) === 'M');
            closingTotal = customerPaid.reduce((sum, d) =>
                sum + (d.price || 0) * (d.quantity || d.laborTime || 0), 0);
            discountedTotal = customerPaid.reduce((sum, d) => sum + (d.acceptedPrice || 0), 0);
        }

        const discountTotal = Math.max(0, closingTotal - discountedTotal);
        const kdvTotal      = discountedTotal * kdvRate;
        const invoiceTotal  = discountedTotal + kdvTotal;

        const totalsBlock = [
            { label: t.closingTotal    || 'Kapanış Toplam Tutar', value: closingTotal,    bold: false },
            { label: t.discountTotal   || 'İndirim Tutarı',       value: discountTotal,   bold: false },
            { label: t.discountedTotal || 'İndirimli Toplam',     value: discountedTotal, bold: false },
            { label: t.kdvTotal        || 'K.D.V Tutarı',         value: kdvTotal,        bold: false },
            { label: t.invoiceTotal    || 'Fatura Toplamı',       value: invoiceTotal,    bold: true  }
        ];

        totalsBlock.forEach(({ label, value, bold }) => {
            const rIdx = rows.length;
            // Sağ tarafta 2 sütunluk blok: kolon 6-7 etiket, kolon 8 değer
            rows.push(padRow([
                empty(), empty(), empty(), empty(), empty(), empty(),
                C(label, bold ? s.totalLabelBold : s.totalLabel),
                empty(),
                C(this.formatNumber(value), bold ? s.totalValueBold : s.totalValue)
            ]));
            merges.push({ s: { r: rIdx, c: 6 }, e: { r: rIdx, c: 7 } });
        });

        // -----------------------------------------------------------------
        // Sayfa oluşturma
        // -----------------------------------------------------------------

        // ws_data formatından worksheet üret. Her hücre {v, s} objesi.
        const ws = {};
        const range = { s: { r: 0, c: 0 }, e: { r: rows.length - 1, c: colCount - 1 } };

        rows.forEach((row, rIdx) => {
            row.forEach((cell, cIdx) => {
                if (cell.v === '' && (!cell.s || Object.keys(cell.s).length === 0)) return; // boş hücreyi atla
                const addr = XLSX.utils.encode_cell({ r: rIdx, c: cIdx });
                const isNum = typeof cell.v === 'number';
                ws[addr] = {
                    v: cell.v,
                    t: isNum ? 'n' : 's',
                    s: cell.s || {}
                };
            });
        });

        ws['!ref']    = XLSX.utils.encode_range(range);
        ws['!merges'] = merges;
        ws['!cols']   = [
            { wch: 6 },   // Tip
            { wch: 10 },  // O.T. / etiket
            { wch: 16 },  // Parça No
            { wch: 32 },  // Açıklama
            { wch: 8 },   // Adet
            { wch: 12 },  // B.Fiyat
            { wch: 14 },  // Tutar
            { wch: 10 },  // İnd%
            { wch: 14 }   // Net Total
        ];
        // Sayfa yazdırma ayarları (PDF ile uyumlu A4 yatay)
        ws['!pageSetup'] = { orientation: 'landscape', paperSize: 9, fitToWidth: 1, fitToHeight: 0 };
        ws['!margins']   = { left: 0.3, right: 0.3, top: 0.4, bottom: 0.4, header: 0.2, footer: 0.2 };

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, options.sheetName || (t.sheetName || 'Onarım Kapanış'));
        return wb;
    }

    /**
     * Workbook'u tarayıcıda indirtir
     */
    download(workbook, filename = 'onarim-kapanis-formu.xlsx') {
        XLSX.writeFile(workbook, filename, { bookType: 'xlsx', cellStyles: true });
    }

    /**
     * Tek adımda üret + indir
     */
    generateAndDownload(data, translations = {}, options = {}) {
        const wb = this.generate(data, translations, options);
        const fileName = options.fileName ||
            `onarim-kapanis-${data.onarimNo || data.id || Date.now()}.xlsx`;
        this.download(wb, fileName);
    }
}

export default RepairClosingExcelForm;
